<script>
  import { ticks, nice } from "d3-array";
  import { arc, pie } from "d3-shape";

  import Table from "./Table.svelte";

  //props
  export let title = "";
  export let data = [];
  export let fill = "#e495ed";
  export let description = "";
  export let source = "Fox News";
  export let source_url = "https://foxnews.com";
  export let brand_logo = "news";
  export let numberFormat = "";
  export let last_updated = "Feb 31, 3033";

  //layout specs, accessors, position calculators
  const PADDING = 2;
  const VIEWPORT_SIZE = 100;
  $: vb = {
    x: 0,
    y: 0,
    w: VIEWPORT_SIZE,
    h: VIEWPORT_SIZE,
  };
  const path = arc()
    .innerRadius(0)
    .outerRadius(VIEWPORT_SIZE / 2 - PADDING);
  $: pie_data = pie().value((d) => d.data)(data);
  $: pie_data_sorted = pie_data.sort(sort_by_value);
  $: small_wedges = pie_data_sorted.filter((d) => is_small_wedge(d));

  //misc
  const SMALL_WEDGE = Math.PI * 2 * 0.05;
  const is_small_wedge = (d) => {
    return d.endAngle - d.startAngle <= SMALL_WEDGE;
  };
  const sort_by_value = (b, a) => a.value - b.value;
  const small_label_style = (d, i) => {
    let [left, top] = path.centroid(d);
    top = top * 2 + 50;
    left = left * 2 + 50;
    let height = `calc(20px + ${i}em)`;
    return `top: ${top}%; left: ${left}%; height: ${height};`;
  };
  const label_offset = (d) => {
    let [left, top] = path.centroid(d);
    top = top * 1.5;
    left = left * 1.5;
    return `top: ${50 + top}%; left: ${50 + left}%`;
  };

  //for testing
  /*
  const set_random_data = () => {
    let out = [];
    let num_slices = 10;
    for (let i = 0; i < num_slices; i++) {
      let a = (i + 1) / num_slices;
      let next = {
        label: `Testers tag${i}`,
        data: Math.random() * 1000,
        fill: random_fill(),
      };
      out.push(next);
    }
    data = out;
  };
  const random_fill = (a = 1.0) => {
    let r = (0xff * Math.random()) | (0 << 4);
    let g = (0xff * Math.random()) | (0 << 2);
    let b = (0xff * Math.random()) | 0;
    return `rgb(${r}, ${g}, ${b})`;
  };
 */
</script>

<!--
<button on:click={set_random_data}>bars</button>
<button on:click={random_fill}>fill</button>
-->

{#if title || description}
  <header class="heading">
    {#if title}
      <h1 class="title">{title}</h1>
    {/if}
    {#if description}
      <p class="dek" id="description">{description}</p>
    {/if}
  </header>
{/if}
<main style={`padding-top: ${small_wedges.length}rem;`}>
  <div id="display_area">
    <svg viewBox="{vb.x} {vb.y} {vb.w} {vb.h}">
      <g transform="translate({vb.w / 2} {vb.h / 2})">
        {#each pie_data_sorted as d, i}
          <path d={path(d)} fill={data[i].fill || fill} />
        {/each}
      </g>
    </svg>
    <div id="labels">
      {#each pie_data_sorted as d, i}
        <span
          class="label"
          style={label_offset(d)}
          class:hidden={is_small_wedge(d)}
        >
          {d.data.label}
        </span>
      {/each}
      {#each small_wedges as d, i}
        <span class="label-line" style={small_label_style(d, i)}>
          <span class="circle" />
          <span class="small-label">
            {d.data.label}
          </span>
        </span>
      {/each}
    </div>
  </div>
  <Table {data} {numberFormat} />
</main>
<footer class="footing">
  {#if source || last_updated}
    <p>
      {#if source}
        <span
          >Source:
          {#if source_url}
            <a href={source_url} target="_top">{source}</a>.
          {:else}
            {source}
          {/if}
        </span>
      {/if}
      {#if last_updated}
        <span>
          Last Updated: {last_updated}.
        </span>
      {/if}
    </p>
  {/if}

  {#if brand_logo === "business"}
    <img
      class="logo"
      width="48px"
      height="36px"
      src="https://static.foxnews.com/static/orion/styles/img/fox-business/s/logos/fox-business-2-color.svg"
      alt="Fox Business"
    />
  {:else}
    <img
      class="logo"
      width="36px"
      height="36px"
      src="https://static.foxnews.com/static/orion/styles/img/fox-news/s/logos/fox-news-logo.svg"
      alt="Fox News"
    />
  {/if}
</footer>

<style>
  .label-line {
    width: 6px;
    border-right: 1px solid #222;
    border-top: 1px solid #222;
    position: absolute;
    transform: translate(-100%, -100%);
  }

  .label-line .circle {
    top: 0;
    left: 0;
    width: 6px;
    height: 6px;
    background-color: #222;
    border-radius: 50%;
    position: absolute;
    transform: translate(-50%, -50%);
  }

  .small-label {
    display: inline-block;
    transform: translate(-100%, -50%);
    font-size: 12px;
    line-height: 20px;
    white-space: nowrap;
    padding: 0 10px;
  }

  .hidden {
    display: none;
  }

  svg {
    width: 100%;
    display: block;
    margin: auto;
    overflow: visible;
  }

  path {
    stroke-width: 0.15px;
    stroke: white;
  }

  #display_area {
    position: relative;
    max-width: 384px;
    margin: auto;
    margin-bottom: 24px;
  }

  #labels {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
  }

  .label {
    --b: 1px;
    position: absolute;
    margin: 0;
    font-size: 16px;
    line-height: 24px;
    color: #ffffff;
    transform: translate(-50%, -50%);
    text-shadow: var(--b) 0 0 #000, calc(-1 * var(--b)) 0 0 #000,
      0 var(--b) 0 #000, 0 calc(-1 * var(--b)) 0 #000, var(--b) var(--b) #000,
      calc(-1 * var(--b)) calc(-1 * var(--b)) 0 #000,
      var(--b) calc(-1 * var(--b)) 0 #000, calc(-1 * var(--b)) var(--b) 0 #000;
    white-space: nowrap;
  }
</style>
